import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, CalendarClock, ArrowLeft } from "lucide-react";
import SUBURBS, { suburbAngle } from "@/data/suburbs";
import { CONTACT, scrollToId } from "@/lib/api";
import { SEO } from "@/components/landing/SEO";
import { Nav } from "@/components/landing/Nav";
import { Marquee } from "@/components/landing/Marquee";
import { Pricing } from "@/components/landing/Pricing";
import { Booking } from "@/components/landing/Booking";
import { FAQ } from "@/components/landing/FAQ";
import { Suburbs } from "@/components/landing/Suburbs";
import { Footer } from "@/components/landing/Footer";
import { useAvailability } from "@/hooks/useAvailability";

export default function SuburbPage() {
  const { slug } = useParams();
  const suburb = SUBURBS.find((s) => s.slug === slug);
  const { data } = useAvailability();

  if (!suburb) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 bg-white">
        <p className="font-bebas text-5xl text-navy">THAT PAGE HAS DRAINED AWAY</p>
        <Link data-testid="suburb-404-home-link" to="/" className="rounded-full bg-brand text-white font-bebas text-xl px-8 py-3 hover:bg-brand-hover transition-colors duration-300">
          BACK TO LEAK DETECTION HQ
        </Link>
      </main>
    );
  }

  return (
    <main>
      <SEO
        title={`Leak Detection ${suburb.name} | $360 Upfront Price | The Plumbing & Electrical Co.`}
        description={`Water & gas leak detection in ${suburb.name}, Perth. $360 first half-hour incl. tool fee & GST, live online booking, acoustic & thermal detection, lifetime labour warranty. We find it, then we fix it.`}
        canonical={`https://leak-detection.com.au/leak-detection/${suburb.slug}`}
      />
      <Nav />
      <section data-testid="suburb-hero" className="hero-grid-bg noise-overlay relative pt-36 pb-20 overflow-hidden">
        <div className="absolute top-24 right-[10%] w-72 h-72 rounded-full bg-brand/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Link data-testid="suburb-back-link" to="/" className="inline-flex items-center gap-2 text-sm font-bold text-navy/50 hover:text-brand transition-colors duration-200 mb-8">
            <ArrowLeft className="w-4 h-4" /> Leak Detection Perth
          </Link>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 font-bold text-xs tracking-[0.25em] text-brand uppercase mb-4"
          >
            <MapPin className="w-4 h-4" /> {suburb.name}, Perth WA
          </motion.p>
          <h1 className="font-bebas tracking-tight text-navy text-[13vw] sm:text-7xl lg:text-8xl leading-[0.88]">
            <span className="mask-line"><motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>LEAK DETECTION</motion.span></span>
            <span className="mask-line"><motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} className="text-brand">{suburb.name.toUpperCase()}</motion.span></span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 max-w-2xl text-navy/70 text-base sm:text-lg"
          >
            {suburb.name} homes have their quirks — think {suburbAngle(suburb.slug)}. Our leak detectives know them all.
            Same deal as always: $360 first half-hour (tool fee & GST included), a rock-up time you pick yourself,
            and licensed plumbers who fix what they find. No dramas, no drip-feed of hidden fees.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="mt-8 flex flex-wrap gap-4">
            <button
              data-testid="suburb-book-button"
              onClick={() => scrollToId("book")}
              className="rounded-full bg-brand text-white font-bebas text-2xl tracking-wide px-10 py-4 hover:bg-brand-hover hover:-translate-y-1 hover:shadow-2xl hover:shadow-brand/40 transition-[transform,box-shadow,background-color] duration-300"
            >
              BOOK {suburb.name.toUpperCase()} LEAK DETECTION
            </button>
            <a
              data-testid="suburb-call-button"
              href={CONTACT.phoneHref}
              className="flex items-center gap-2 rounded-full border-2 border-navy text-navy font-bebas text-2xl tracking-wide px-8 py-[14px] hover:bg-navy hover:text-white transition-[background-color,color] duration-300"
            >
              <Phone className="w-5 h-5" /> {CONTACT.phone}
            </a>
            {data?.next_available && (
              <span data-testid="suburb-next-slot" className="flex items-center gap-2 text-sm font-bold text-navy/60">
                <CalendarClock className="w-4 h-4 text-brand" /> Next available: {data.next_available.label}
              </span>
            )}
          </motion.div>
        </div>
      </section>
      <Marquee />
      <Pricing />
      <Booking page={`suburb:${suburb.slug}`} defaultSuburb={suburb.name} />
      <FAQ limit={4} />
      <Suburbs />
      <Footer />
    </main>
  );
}
